import * as React from 'react'

import { monthKeysShort } from '@/modules/shared/constants/months'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { format } from 'date-fns'
import { faChevronLeftSolid, faChevronRightSolid, FaIcon } from '@/lib/fa'

interface Props {
  value: string | null
  onConfirm: (newValue: string) => void
  confirmSelection?: boolean
}

const MonthPicker: React.FC<Props> = ({
  value,
  onConfirm,
  confirmSelection = false,
}) => {
  // Trzy strażniki (`!localizeContext`, `!t`, `!monthKeysShort`) stały tu nad hookami
  // i psuły ich kolejność. Wszystkie były martwe: `useLocalize` rzuca wyjątkiem zamiast
  // zwracać `undefined`, a `monthKeysShort` to statyczny import ze stałych.
  const { t } = useLocalize()

  const [year, month] = value
    ? value.split('-').map((v) => parseInt(v, 10))
    : [new Date().getFullYear(), new Date().getMonth() + 1]

  const [selectedMonth, setSelectedMonth] = React.useState(month)
  const [viewYear, setViewYear] = React.useState(year)

  const handlePrevYear = () => setViewYear((prev) => prev - 1)
  const handleNextYear = () => setViewYear((prev) => prev + 1)
  const handleSelectMonth = (m: number) => {
    setSelectedMonth(m)
    if (!confirmSelection) {
      const newDate = new Date(viewYear, m - 1, 1)
      const formatted = format(newDate, 'YYYY-MM')
      onConfirm(formatted)
    }
  }

  const handleConfirm = () => {
    const newDate = new Date(viewYear, selectedMonth - 1, 1)
    const formatted = format(newDate, 'YYYY-MM')
    onConfirm(formatted)
  }

  return (
    <div className="tw-h-fit tw-w-fit tw-rounded-lg tw-bg-white tw-shadow-md">
      <div className="tw-w-[340px]">
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
          <FaIcon
            icon={faChevronLeftSolid}
            className="tw-w-5 tw-cursor-pointer hover:tw-opacity-90"
            onClick={handlePrevYear}
          />

          <p className="tw-font-semibold">{viewYear}</p>
          <FaIcon
            icon={faChevronRightSolid}
            className="tw-w-5 tw-cursor-pointer hover:tw-opacity-90"
            onClick={handleNextYear}
          />
        </div>

        <div className="tw-grid tw-grid-cols-4 tw-gap-2 tw-px-4 tw-pb-4">
          {monthKeysShort.map((m, i) => (
            <div
              key={m}
              onClick={() => handleSelectMonth(i + 1)}
              className={`tw-w-full tw-cursor-pointer tw-rounded tw-border-2 tw-border-transparent tw-py-4 tw-text-center tw-font-normal ${
                selectedMonth === i + 1
                  ? '!tw-border-primary tw-bg-secondary tw-font-semibold tw-text-primary'
                  : 'tw-bg-transparent tw-text-content-base/90'
              } hover:tw-bg-base-300 hover:tw-font-semibold`}
            >
              {t(`${m}`)}
            </div>
          ))}
        </div>
        {confirmSelection && (
          <div className="tw-flex tw-justify-end tw-p-3">
            <button
              onClick={handleConfirm}
              className="tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90"
            >
              {t('common.confirm')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default MonthPicker
